import {
    DATIQ_SCHEDULER_RULES_TYPE_KEY,
    DatiqSchedulerConditionKeys,
    datiqSchedulerGetTimeFormat,
} from '@dat/datiq-scheduler';
import {
    DATIQ_DEFAULT_STRING,
    DATIQ_PAGE_TITLE,
    DatiqDayOfWeek,
    datiqInputCreateId,
    datiqIsDayOfWeek,
    DatiqRulesConditionOperators,
    DatiqRulesIsTypes,
    DatiqRulesRelations,
    IDatiqGroupBy,
    IDatiqRulesCondition,
    IDatiqRulesData,
    IDatiqRulesRule,
} from '@dat/datiq-core/models';

import {
    DatiqMcDirections,
    DatiqMcEquipmentTypes,
    DatiqMcGeographyTypes,
    DatiqMcPanelInfoMessageClassNames,
    DatiqMcTimeTypes,
} from './datiq-mc.enum';
import {
    IDatiqMcLtData,
    IDatiqMcMarketData,
    IDatiqMcPanelInfoMessage,
    IDatiqMcQueryParams,
} from './datiq-mc.interface';
import { DateTime } from 'luxon';

export const DATIQ_MC_ROUTE: string = 'market-conditions';
export const DATIQ_MC_QUERY_PARAMS_ID_SUFFIX: string = 'datiq-mc-query-params';
export const DATIQ_MC_QUERY_PARAMS_ID_SEPARATOR: string = '__';

export const DATIQ_MC_CONDITIONS_URL_PARTS: string[] = [
    'market-conditions',
    'conditions',
];

export const DATIQ_MC_QUERY_PARAMS_ID: (id: string) => string = (id: string) =>
    `${
        !!id ? id + DATIQ_MC_QUERY_PARAMS_ID_SEPARATOR : ''
    }${DATIQ_MC_QUERY_PARAMS_ID_SUFFIX}`;

export const DATIQ_MC_DEFAULT_GEO_CODE: string = 'CO_DEN';

export const DATIQ_MC_MAP: string = 'datiq-mc-map';
export const DATIQ_MC_MAP_ID: (id: string) => string = (id: string) =>
    datiqInputCreateId([DATIQ_MC_MAP, id]);

export const emptyDatiqMcMarketData: IDatiqMcMarketData = {
    geoType: DatiqMcGeographyTypes.MarketArea,
    geoCode: DATIQ_MC_DEFAULT_GEO_CODE,
    equipmentType: DatiqMcEquipmentTypes.Van,
    timeType: DatiqMcTimeTypes.Forecast,
    direction: DatiqMcDirections.Out,
};

export const emptyDatiqMcQueryParams: IDatiqMcQueryParams = {
    direction: DatiqMcDirections.Out,
    equipmentType: DatiqMcEquipmentTypes.Van,
    geoType: DatiqMcGeographyTypes.MarketArea,
    timeType: DatiqMcTimeTypes.PreviousBusinessDay,
    forecastDaysOut: 0,
};

export const DatiqMcDisplayDirection: { [key: string]: string } = {
    [DatiqMcDirections.Out]: 'Outbound',
    [DatiqMcDirections.In]: 'Inbound',
};

export const DatiqMcDisplayTimeType: { [key: string]: string } = {
    [DatiqMcTimeTypes.Current]: 'Current',
    [DatiqMcTimeTypes.Forecast]: 'Forecast',
    [DatiqMcTimeTypes.PreviousBusinessDay]: 'Prior Business Day',
    [DatiqMcTimeTypes.EightDaysBack]: 'Prior 8 Days',
    [DatiqMcTimeTypes.ThirtyDaysBack]: 'Prior 30 Days',
};

export const DatiqMcDashboardDisplayTimeType: { [key: string]: string } = {
    [DatiqMcTimeTypes.Current]: 'Current',
    [DatiqMcTimeTypes.Forecast]: 'Forecast',
    [DatiqMcTimeTypes.PreviousBusinessDay]: 'Last Business Day',
    [DatiqMcTimeTypes.EightDaysBack]: 'Last 8 Days',
    [DatiqMcTimeTypes.ThirtyDaysBack]: 'Last 30 Days',
};

export const DatiqMcDisplayEquipmentType: { [key: string]: string } = {
    [DatiqMcEquipmentTypes.Flatbed]: 'Flatbeds',
    [DatiqMcEquipmentTypes.Reefer]: 'Reefer',
    [DatiqMcEquipmentTypes.Van]: 'Van',
};

export const DatiqMcDisplayGeographyType: { [key: string]: string } = {
    [DatiqMcGeographyTypes.Country]: 'Country',
    [DatiqMcGeographyTypes.ExtendedMarketArea]: 'Extended Market',
    [DatiqMcGeographyTypes.MarketArea]: 'Market',
    [DatiqMcGeographyTypes.Region]: 'Region',
    [DatiqMcGeographyTypes.State]: 'State',
    [DatiqMcGeographyTypes.ThreeDigitZip]: '3-Digit Zip',
};

export const DATIQ_PAGE_TITLE_MC: string =
    DATIQ_PAGE_TITLE('Market Conditions');
export const DATIQ_ANALYTICS_PAGE_NAME_MC: string = 'Market Conditions';

// Data is unavailable roughly between midnight and 4am PT ignoring daylight savings time
// convert PT to UTC (-8), set hours to midnight and 4am, & convert to local time

const datiqMcUnavailable = DateTime.now()
    .setZone('UTC-8')
    .set({ hour: 0, minute: 0 })
    .toLocal()
    .toJSDate();

const datiqMcAvailable = DateTime.now()
    .setZone('UTC-8')
    .set({ hour: 4, minute: 0 })
    .toLocal()
    .toJSDate();

export const getDatiqMCPageTimeRules = (
    relation: DatiqRulesRelations
): IDatiqRulesData<unknown>[] => {
    return [
        {
            relation,
            rules: [
                {
                    conditionOperator: DatiqRulesConditionOperators.And,
                    conditions: [
                        {
                            type: DATIQ_SCHEDULER_RULES_TYPE_KEY,
                            key: DatiqSchedulerConditionKeys.TimeOfDay,
                            is: DatiqRulesIsTypes.Later,
                            value: datiqSchedulerGetTimeFormat(
                                datiqMcUnavailable
                            ),
                        },
                        {
                            type: DATIQ_SCHEDULER_RULES_TYPE_KEY,
                            key: DatiqSchedulerConditionKeys.TimeOfDay,
                            is: DatiqRulesIsTypes.Earlier,
                            value: datiqSchedulerGetTimeFormat(
                                datiqMcAvailable
                            ),
                        },
                    ] as IDatiqRulesCondition[],
                },
            ] as IDatiqRulesRule[],
        },
    ];
};

export const datiqMcUnavailableRules: IDatiqRulesData<unknown>[] =
    getDatiqMCPageTimeRules(DatiqRulesRelations.All);

export const datiqMcAvailableRules: IDatiqRulesData<unknown>[] =
    getDatiqMCPageTimeRules(DatiqRulesRelations.None);

export const DATIQ_MC_PANEL_INFO_MESSAGE_FORECAST_UPDATING: string =
    'MCI Forecast is updating and will be available soon.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_INBOUND_UPDATING: string =
    'MCI Inbound is updating and will be available soon.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_SHOWING_LOAD_TO_TRUCK: string =
    'Showing Load to Truck results. MCI is updating and will be available soon.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_NEW_SELECTION: string =
    'Please make a new selection.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_SELECT_AREA: string =
    'Select an area on the map or enter a location to see results.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_3DZ_FORECAST: string =
    '3 Digit Zip is currently unavailable for the MCI 8 Day Forecast.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_SELECT_NEW_TIMEFRAME_OR_AREA: string =
    'Please select a different Timeframe or Area.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_3DZ_LOAD_TRUCK: string =
    'Showing Load to Truck results. MCI 3 Digit Zip is currently unavailable.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_3DZ_INBOUND: string =
    '3 Digit Zip MCI Inbound is unavailable.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_DIFFERENT_AREA: string =
    'Please select a different Area.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_MC_DATASOURCE_DISABLED: string =
    'MCI is currently unavailable.';
export const DATIQ_MC_PANEL_INFO_MESSAGE_LT_DATASOURCE_DISABLED: string =
    'Load to Truck results is currently unavailable. MCI is updating and will be available soon';

export const DATIQ_LT_LINE_CHART_INFO_MESSAGE_INVALID_TIMETYPE: string = 
'L/T Ratio Data not available for this selection. Filter to Outbound Last 8 and 30 Day filters to view.'
export const DATIQ_MCI_LINE_CHART_INFO_MESSAGE_INVALID_TIMETYPE: string = 
'MCI Graph Data not available for this selection. Filter to Last 8 and 30 Day filters to view.'

export const emptyDatiqMcPanelInfoMessage: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_DEFAULT_STRING,
    },
];

export const datiqMcPanelInfoMessage3DZForecast: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_3DZ_FORECAST,
    },
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_SELECT_NEW_TIMEFRAME_OR_AREA,
        className: DatiqMcPanelInfoMessageClassNames.ActionRequired,
    },
];

export const datiqMcPanelInfoMessage3DZCurrentInbound: IDatiqMcPanelInfoMessage =
    [
        {
            text: DATIQ_MC_PANEL_INFO_MESSAGE_3DZ_INBOUND,
        },
        {
            text: DATIQ_MC_PANEL_INFO_MESSAGE_DIFFERENT_AREA,
            className: DatiqMcPanelInfoMessageClassNames.ActionRequired,
        },
    ];

export const datiqMcPanelInfoMessage3DZCurrentOutbound: IDatiqMcPanelInfoMessage =
    [
        {
            text: DATIQ_MC_PANEL_INFO_MESSAGE_3DZ_LOAD_TRUCK,
        },
    ];

export const datiqMcPanelInfoMessageNoSelection: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_SELECT_AREA,
        className: DatiqMcPanelInfoMessageClassNames.ActionRequired,
    },
];

export const datiqMcPanelInfoMessageLTForecast: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_FORECAST_UPDATING,
    },
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_NEW_SELECTION,
        className: DatiqMcPanelInfoMessageClassNames.ActionRequired,
    },
];

export const datiqMcPanelInfoMessageLTOutbound: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_SHOWING_LOAD_TO_TRUCK,
    },
];

export const datiqMcPanelInfoMessageLTInbound: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_INBOUND_UPDATING,
    },
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_NEW_SELECTION,
        className: DatiqMcPanelInfoMessageClassNames.ActionRequired,
    },
];

export const datiqMcPanelInfoMessageMciDataSourceDisabled: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_MC_DATASOURCE_DISABLED,
    },
];

export const datiqMcPanelInfoMessageLtDataSourceDisabled: IDatiqMcPanelInfoMessage = [
    {
        text: DATIQ_MC_PANEL_INFO_MESSAGE_LT_DATASOURCE_DISABLED
    },
]

const datiqMcLtFilterDataByDay = (
    data: IDatiqMcLtData[],
    dayOfWeek: DatiqDayOfWeek
): IDatiqMcLtData[] => {
    return data.filter((item) =>
        datiqIsDayOfWeek(new Date(item.date), dayOfWeek)
    );
};

export const datiqMcLtFilterGroupedBy = (
    data: IDatiqMcLtData[],
    options: IDatiqGroupBy
): IDatiqMcLtData[] => {
    const getDataForDaysOfWeek = () =>
        datiqMcLtFilterDataByDay(data, options.dayOfWeek).slice(
            options.past ? -options.count : options.count
        );
    const hasData = data && data.length;
    const hasOptions = Boolean(options);
    const hasDataAndOptions = hasData && hasOptions;

    return hasDataAndOptions ? getDataForDaysOfWeek() : hasData ? data : [];
};
